/**
 * This file is the entry point for the React app, it sets up the root
 * element and renders the App component to the DOM.
 *
 * It is included in `src/index.html`.
 */

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { App } from "./App";
import { PostHogProvider } from "posthog-js/react";
import { captureError, installFetchErrorTracking } from "./errorTracking";

const elem = document.getElementById("root")!;

// Bun's substitution (below) only fires for variables that are actually SET.
// An unset one is left as a real `process` reference, which the browser has no
// binding for:
//
//     Uncaught ReferenceError: process is not defined
//
// dev has no PostHog keys at all, so it is the environment that hits this;
// a fresh checkout with no .env is the other. Whether it actually bites
// depends on the Bun version doing the bundling - 1.3.3 substitutes an unset
// var away, 1.3.14 did not - which is not a thing to depend on in either
// direction.
//
// Defining the global first makes an unsubstituted read evaluate to undefined
// instead of throwing, while leaving the literal member expressions below
// exactly as they are, so substitution still happens wherever it works. The
// shim is inert once substituted: nothing reads it.
//
// Safe for the libraries too. React and posthog-js both probe with
// `typeof process === "object" && typeof process.emit === "function"`, and an
// env-only object fails the second half exactly as a missing global fails the
// first.
const globalScope = globalThis as { process?: { env?: Record<string, string | undefined> } };
globalScope.process ??= {};
globalScope.process.env ??= {};

// These MUST be written as the literal text `process.env.BUN_PUBLIC_*`.
//
// Bun's substitution is textual: it replaces that exact member expression with
// a string literal at bundle time, leaving no `process` reference in the
// output. Read the same value through a variable - `const env = ...process.env`
// then `env.BUN_PUBLIC_KEY` - and there is no static pattern to match, so the
// bundler leaves it alone and the browser evaluates it to undefined. PostHog
// then silently never initialises, which is exactly what happened here.
//
// Verified with `bun build --env='BUN_PUBLIC_*'`:
//   var viaVariable = env.BUN_PUBLIC_POSTHOG_KEY;   // untouched
//   var viaLiteral  = "phc_...";                     // substituted
//
// This is also why the form below cannot throw the "process is not defined"
// ReferenceError the indirect version was written to avoid: after
// substitution there is no `process` left to resolve.
const posthogKey = process.env.BUN_PUBLIC_POSTHOG_KEY;
const posthogHost = process.env.BUN_PUBLIC_POSTHOG_HOST || "https://us.i.posthog.com";

if (posthogKey) installFetchErrorTracking(posthogHost);

// A render error with no error boundary above it unmounts the whole app.
// React would hand it to window.reportError, where capture_exceptions picks it
// up, but without the component stack -- which, in a minified bundle, is the
// only clue to which component threw. Reported here instead, with it; still
// logged to the console the way React's default does.
const rootOptions = {
  onUncaughtError(error: unknown, info: { componentStack?: string }) {
    console.error(error);
    captureError(error, { react_component_stack: info.componentStack, unhandled: true });
  },
};

const app = (
  <StrictMode>
    {posthogKey ? (
      <PostHogProvider
        apiKey={posthogKey}
        options={{
          api_host: posthogHost,
          defaults: "2025-05-24",
          capture_exceptions: true,
          debug: process.env.NODE_ENV === "development",
        }}
      >
        <App />
      </PostHogProvider>
    ) : (
      <App />
    )}
  </StrictMode>
);

if (import.meta.hot) {
  // With hot module reloading, `import.meta.hot.data` is persisted.
  const root = (import.meta.hot.data.root ??= createRoot(elem, rootOptions));
  root.render(app);
} else {
  // The hot module reloading API is not available in production.
  createRoot(elem, rootOptions).render(app);
}